"use client";

import React, { useState } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import { KeyResultsForm } from "@/components/goals/key-results-form";
import {
  updateGoalAction,
  deleteGoalAction,
  deleteKeyResultAction,
} from "@/actions/goals-actions";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useBreadcrumbStore } from "@/components/layout/breadcrumb";
import {
  ArrowLeft,
  Calendar,
  User,
  Plus,
  Trash2,
  Edit3,
  TrendingUp,
  TrendingDown,
  Minus,
  Sparkles,
  AlertTriangle,
} from "lucide-react";

interface GoalDetail {
  id: string;
  title: string;
  progress: number;
  trend: "stable" | "up" | "down";
  targetDate: string;
  status: "healthy" | "attention" | "at_risk" | "critical";
  confidenceForecast: number;
  ownerId: string | null;
  ownerName: string | null;
}

interface KeyResultDetail {
  id: string;
  goalId: string;
  title: string;
  progress: number;
  targetValue: number;
  currentValue: number;
  unit: string;
}

interface AgentItem {
  id: string;
  name: string;
}

interface GoalDetailsClientProps {
  goal: GoalDetail;
  keyResults: KeyResultDetail[];
  agents: AgentItem[];
}

export function GoalDetailsClient({
  goal,
  keyResults,
  agents,
}: GoalDetailsClientProps) {
  const router = useRouter();
  const setLabel = useBreadcrumbStore((state) => state.setLabel);
  React.useEffect(() => {
    if (goal?.title) {
      setLabel(goal.id, goal.title);
    }
  }, [goal?.id, goal?.title, setLabel]);

  const [isEditGoalOpen, setIsEditGoalOpen] = useState(false);
  const [isAddKrOpen, setIsAddKrOpen] = useState(false);
  const [editingKr, setEditingKr] = useState<KeyResultDetail | null>(null);

  // Goal Form State
  const [goalTitle, setGoalTitle] = useState(goal.title);
  const [goalTargetDate, setGoalTargetDate] = useState(
    goal.targetDate.split("T")[0],
  );
  const [goalStatus, setGoalStatus] = useState(goal.status);
  const [goalConfidence, setGoalConfidence] = useState(
    goal.confidenceForecast.toString(),
  );
  const [goalOwnerId, setGoalOwnerId] = useState(goal.ownerId || "");
  const [goalLoading, setGoalLoading] = useState(false);
  const [goalError, setGoalError] = useState("");

  const handleEditGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    setGoalError("");

    if (!goalTitle.trim()) {
      setGoalError("Il titolo è obbligatorio.");
      return;
    }
    const confidence = parseInt(goalConfidence);
    if (isNaN(confidence) || confidence < 0 || confidence > 100) {
      setGoalError("La confidenza stimata deve essere compresa tra 0 e 100.");
      return;
    }

    setGoalLoading(true);
    try {
      const res = await updateGoalAction({
        id: goal.id,
        title: goalTitle,
        targetDate: goalTargetDate,
        status: goalStatus,
        confidenceForecast: confidence,
        ownerId: goalOwnerId || null,
      });

      if (res.error) {
        setGoalError(res.error);
      } else {
        setIsEditGoalOpen(false);
        router.refresh();
      }
    } catch (err: any) {
      setGoalError(err.message || "Impossibile aggiornare l'obiettivo.");
    } finally {
      setGoalLoading(false);
    }
  };

  const handleDeleteGoal = async () => {
    if (
      !confirm(
        "Sei sicuro di voler eliminare questo obiettivo strategico? Questa operazione eliminerà anche tutti i relativi Key Results.",
      )
    )
      return;

    try {
      const res = await deleteGoalAction(goal.id);
      if (res.error) {
        alert(res.error);
      } else {
        router.push("/strategy/goals");
      }
    } catch (err: any) {
      alert(err.message || "Errore durante l'eliminazione dell'obiettivo.");
    }
  };

  const handleDeleteKr = async (krId: string) => {
    if (!confirm("Sei sicuro di voler eliminare questo Key Result?")) return;

    try {
      const res = await deleteKeyResultAction(krId, goal.id);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err: any) {
      alert(err.message || "Errore durante l'eliminazione.");
    }
  };

  const getStatusBadge = (status: GoalDetail["status"]) => {
    switch (status) {
      case "healthy":
        return <Badge variant="success">Sano</Badge>;
      case "attention":
        return <Badge variant="warning">Attenzione</Badge>;
      case "at_risk":
        return <Badge variant="error">A Rischio</Badge>;
      case "critical":
        return <Badge variant="error">Critico</Badge>;
    }
  };

  const getTrendIcon = (trend: GoalDetail["trend"]) => {
    switch (trend) {
      case "up":
        return <TrendingUp className="h-5 w-5 text-success" />;
      case "down":
        return <TrendingDown className="h-5 w-5 text-error" />;
      case "stable":
      default:
        return <Minus className="h-5 w-5 text-text-muted" />;
    }
  };

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="space-y-6">
      {/* Back button & Action buttons */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Link
          href="/strategy/goals"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Torna all'elenco
        </Link>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditGoalOpen(true)}
            className="gap-1.5"
          >
            <Edit3 className="h-4 w-4" />
            Modifica
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDeleteGoal}
            className="gap-1.5"
          >
            <Trash2 className="h-4 w-4" />
            Elimina
          </Button>
        </div>
      </div>

      {/* Goal Detail Card */}
      <Card className="border-l-4 border-l-primary-600">
        <CardHeader className="pb-4">
          <div className="flex justify-between items-start gap-4">
            <div className="space-y-1">
              <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                Dettaglio Obiettivo
              </span>
              <h2 className="text-2xl font-bold tracking-tight text-text-primary leading-tight">
                {goal.title}
              </h2>
            </div>
            <div className="flex items-center gap-1.5 shrink-0 bg-card p-1.5 rounded-lg border border-border">
              {getTrendIcon(goal.trend)}
              {getStatusBadge(goal.status)}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm font-semibold">
              <span className="text-text-secondary">
                Avanzamento OKR (Media Key Results)
              </span>
              <span className="text-text-primary">{goal.progress}%</span>
            </div>
            <div className="h-3 w-full bg-border-strong/20 rounded-full overflow-hidden">
              <div
                className="bg-gradient-to-r from-primary-500 to-primary-600 h-full rounded-full transition-all duration-500"
                style={{ width: `${goal.progress}%` }}
              />
            </div>
          </div>

          {/* Quick Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-border pt-4 text-sm">
            <div className="space-y-1">
              <span className="text-text-secondary text-xs font-semibold">
                Scadenza Target
              </span>
              <div className="flex items-center gap-1.5 text-text-primary font-medium">
                <Calendar className="h-4 w-4 text-text-muted" />
                {formatDate(goal.targetDate)}
              </div>
            </div>

            <div className="space-y-1">
              <span className="text-text-secondary text-xs font-semibold">
                Owner Strategico
              </span>
              {goal.ownerId ? (
                <Link
                  href={`/people/${goal.ownerId}`}
                  className="flex items-center gap-1.5 text-primary-600 hover:text-primary-700 transition-colors font-medium hover:underline"
                >
                  <User className="h-4 w-4 text-primary-600" />
                  {goal.ownerName || "Non assegnato"}
                </Link>
              ) : (
                <div className="flex items-center gap-1.5 text-text-secondary font-medium">
                  <User className="h-4 w-4 text-text-muted" />
                  Non assegnato
                </div>
              )}
            </div>

            <div className="space-y-1">
              <span className="text-text-secondary text-xs font-semibold">
                Confidenza Previsione
              </span>
              <div className="text-text-primary font-medium">
                {goal.confidenceForecast}%{" "}
                <span className="text-xs text-text-secondary">
                  di confidenza
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Results Section */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-bold tracking-tight text-text-primary">
              Indicatori di Risultato (Key Results)
            </h3>
            <p className="text-xs text-text-secondary">
              Metriche quantitative collegate a questo obiettivo strategico.
            </p>
          </div>
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() => {
              setEditingKr(null);
              setIsAddKrOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Nuovo Key Result
          </Button>
        </div>

        {keyResults.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 border border-dashed border-border rounded-2xl bg-card/40 text-center">
            <Sparkles className="h-8 w-8 text-primary-500 mb-2 animate-pulse" />
            <h4 className="font-bold text-text-primary text-sm">
              Nessun Key Result configurato
            </h4>
            <p className="text-xs text-text-secondary mt-1 max-w-xs mx-auto">
              Per far progredire questo obiettivo strategico, aggiungi almeno un
              indicatore di risultato quantificabile.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="mt-4 gap-1.5"
              onClick={() => {
                setEditingKr(null);
                setIsAddKrOpen(true);
              }}
            >
              <Plus className="h-3.5 w-3.5" />
              Aggiungi il Primo
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {keyResults.map((kr) => (
              <Card
                key={kr.id}
                className="relative overflow-hidden bg-card/60 hover:bg-card hover:shadow-card/10 transition-all"
              >
                <CardContent className="p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="space-y-3 flex-1 min-w-0">
                    <div className="flex items-start justify-between sm:justify-start gap-3">
                      <div className="space-y-0.5">
                        <h4 className="font-bold text-text-primary text-sm leading-snug">
                          {kr.title}
                        </h4>
                        <span className="text-xs text-text-secondary">
                          Stato attuale: <strong>{kr.currentValue}</strong> di{" "}
                          <strong>
                            {kr.targetValue} {kr.unit}
                          </strong>
                        </span>
                      </div>
                      <Badge variant="neutral" className="shrink-0">
                        {kr.progress}%
                      </Badge>
                    </div>

                    {/* mini progress bar */}
                    <div className="h-1.5 w-full bg-border-strong/15 rounded-full overflow-hidden">
                      <div
                        className="bg-primary-500 h-full rounded-full transition-all duration-300"
                        style={{ width: `${kr.progress}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 self-end sm:self-center shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-text-secondary hover:text-text-primary"
                      onClick={() => {
                        setEditingKr(kr);
                        setIsAddKrOpen(true);
                      }}
                    >
                      <Edit3 className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-error hover:text-error/80 hover:bg-error-soft/10"
                      onClick={() => handleDeleteKr(kr.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit Goal Dialog */}
      <Dialog open={isEditGoalOpen} onOpenChange={setIsEditGoalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Obiettivo</DialogTitle>
            <DialogDescription>
              Aggiorna le informazioni strategiche di questo obiettivo.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleEditGoal} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="edit-title">Titolo Obiettivo</Label>
              <Input
                id="edit-title"
                value={goalTitle}
                onChange={(e) => setGoalTitle(e.target.value)}
                disabled={goalLoading}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="edit-date">Scadenza Target</Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={goalTargetDate}
                  onChange={(e) => setGoalTargetDate(e.target.value)}
                  disabled={goalLoading}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-status">Stato</Label>
                <Select
                  id="edit-status"
                  value={goalStatus}
                  onChange={(e) => setGoalStatus(e.target.value as any)}
                  disabled={goalLoading}
                >
                  <option value="healthy">Sano (Healthy)</option>
                  <option value="attention">Attenzione</option>
                  <option value="at_risk">A Rischio</option>
                  <option value="critical">Critico</option>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="edit-owner">Proprietario (Owner)</Label>
                <Select
                  id="edit-owner"
                  value={goalOwnerId}
                  onChange={(e) => setGoalOwnerId(e.target.value)}
                  disabled={goalLoading}
                >
                  <option value="">Nessuno (Non assegnato)</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-confidence">Confidenza Stimata (%)</Label>
                <Input
                  id="edit-confidence"
                  type="number"
                  min="0"
                  max="100"
                  value={goalConfidence}
                  onChange={(e) => setGoalConfidence(e.target.value)}
                  disabled={goalLoading}
                  required
                />
              </div>
            </div>

            {goalError && <ErrorMessage>{goalError}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsEditGoalOpen(false)}
                disabled={goalLoading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={goalLoading}>
                Salva Modifiche
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Key Result Dialog */}
      <Dialog open={isAddKrOpen} onOpenChange={setIsAddKrOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingKr ? "Modifica Key Result" : "Nuovo Key Result"}
            </DialogTitle>
            <DialogDescription>
              {editingKr
                ? "Aggiorna la metrica del Key Result per allineare l'avanzamento strategico."
                : "Definisci un indicatore numerico quantificabile per misurare il raggiungimento del target."}
            </DialogDescription>
          </DialogHeader>

          <div className="my-2">
            <KeyResultsForm
              goalId={goal.id}
              initialData={
                editingKr
                  ? {
                      id: editingKr.id,
                      title: editingKr.title,
                      currentValue: editingKr.currentValue,
                      targetValue: editingKr.targetValue,
                      unit: editingKr.unit,
                    }
                  : undefined
              }
              onSuccess={() => {
                setIsAddKrOpen(false);
                setEditingKr(null);
                router.refresh();
              }}
              onCancel={() => {
                setIsAddKrOpen(false);
                setEditingKr(null);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
